import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { ChartConfiguration, ChartData, ChartType } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { Ticket, TicketAggregate } from 'src/app/models/ticket/ticket.model';
import { getTicketType } from 'src/app/models/ticket/ticket.util';
import { getComcastChartColorByIndex } from 'src/app/shared/constants/chart-colors';

@Component({
  selector: 'app-ticket-type-chart',
  templateUrl: './ticket-type-chart.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TicketTypeChartComponent {
  readonly tickets = input<Ticket[]>();

  readonly ticketAggregate = input<TicketAggregate | null>(null);

  readonly isLoading = input<boolean>(false);

  private readonly chart = viewChild(BaseChartDirective);

  private readonly typeOrder = ['Care', 'Finance', 'Proactive', 'Support'];

  private readonly typeLabels: Record<string, string> = {
    Care: 'General Inquiry',
    Finance: 'Billing & Payment',
    Proactive: 'Proactive',
    Support: 'Support (NOC)',
  };

  private readonly typeColors: Record<string, string> = {
    Care: getComcastChartColorByIndex(0),
    Finance: getComcastChartColorByIndex(1),
    Proactive: getComcastChartColorByIndex(2),
    Support: getComcastChartColorByIndex(3),
  };

  pieChartData: ChartData<'doughnut'> = {
    labels: [],
    datasets: [
      {
        data: [],
        label: 'Tickets',
        borderWidth: 0,
        hoverBorderWidth: 0,
        backgroundColor: this.typeOrder.map((type) => this.typeColors[type]),
      },
    ],
  };

  readonly pieChartType: ChartType = 'doughnut';

  readonly pieChartOptions: ChartConfiguration<'doughnut'>['options'] = {
    maintainAspectRatio: false,
    responsive: true,
    cutout: '62%',
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        backgroundColor: 'rgba(17, 24, 39, 0.96)',
        titleColor: '#FFFFFF',
        bodyColor: '#FFFFFF',
        titleFont: {
          family: 'Montserrat',
          size: 14,
          weight: 'bold',
        },
        bodyFont: {
          family: 'Lato',
          size: 13,
          weight: 'normal',
        },
        padding: { top: 12, right: 14, bottom: 12, left: 14 },
        caretPadding: 8,
        caretSize: 8,
        cornerRadius: 8,
        boxPadding: 6,
        bodySpacing: 6,
        titleMarginBottom: 8,
        displayColors: true,
        usePointStyle: true,
        callbacks: {
          label: (context) => `Tickets: ${context.parsed}`,
          labelPointStyle: () => ({ pointStyle: 'rectRounded', rotation: 0 }),
        },
      },
    },
    layout: { padding: 4 },
  };

  readonly legendItems = signal<
    { label: string; color: string; count: number }[]
  >([]);

  readonly chartAriaLabel = computed(() => {
    const items = this.legendItems();
    if (!items.length) return 'No ticket type data available';
    return `Ticket type breakdown: ${items.map((item) => `${item.label} ${item.count}`).join(', ')}.`;
  });

  constructor() {
    effect(() => {
      const tickets = this.tickets();
      if (tickets !== undefined) {
        const counts = this.countTicketsByType(tickets);
        const labels = Object.keys(counts);
        untracked(() =>
          this.updateChart(
            labels,
            labels.map((k) => counts[k] ?? 0),
          ),
        );
        return;
      }

      const aggregate = this.ticketAggregate();
      const filtered = this.filterAggregate(aggregate);
      const labels = Object.keys(filtered);
      untracked(() =>
        this.updateChart(
          labels,
          labels.map((k) => filtered[k] ?? 0),
        ),
      );
    });
  }

  private filterAggregate(
    aggregate: TicketAggregate | null,
  ): Partial<TicketAggregate> {
    if (!aggregate) return {};
    const skip = new Set(['total', 'open', 'nonProactive', 'closed']);
    return Object.entries(aggregate).reduce((acc, [key, value]) => {
      if (!skip.has(key) && value !== 0) {
        acc[key.charAt(0).toUpperCase() + key.slice(1)] = value;
      }
      return acc;
    }, {} as Partial<TicketAggregate>);
  }

  private countTicketsByType(tickets: Ticket[]): Record<string, number> {
    return tickets.reduce<Record<string, number>>((acc, ticket) => {
      const type = getTicketType(ticket)[0];
      if (type?.length) acc[type] = (acc[type] ?? 0) + 1;
      return acc;
    }, {});
  }

  private updateChart(labels: string[], data: number[]): void {
    const dataByType: Record<string, number> = {};
    labels.forEach((label, i) => (dataByType[label] = data[i]));

    const chartData = this.typeOrder.map((type) => dataByType[type] ?? 0);
    const isEmpty = !chartData.some((v) => v > 0);

    this.pieChartData.labels = isEmpty
      ? []
      : this.typeOrder.map((t) => this.typeLabels[t]);
    this.pieChartData.datasets[0].data = isEmpty ? [] : chartData;
    this.pieChartData.datasets[0].backgroundColor = this.typeOrder.map(
      (t) => this.typeColors[t],
    );
    this.chart()?.update();

    const total = chartData.reduce((s, v) => s + v, 0);
    this.legendItems.set(
      total === 0
        ? []
        : this.typeOrder
            .map((type, i) => ({
              label: this.typeLabels[type],
              color: this.typeColors[type],
              count: chartData[i],
            }))
            .filter((item) => item.count > 0),
    );
  }
}
